import React, { useCallback, useEffect, useState } from "react";
import apiUrl from "../../../Apiurl";
import { useAuth0 } from "@auth0/auth0-react";
import ImageZoomModal from "../../../interfaccia/ImageZoomModal";
import BlockSkeleton from "../../../interfaccia/loading/BlockSkeleton";

const imageCache = new Map();

function normalizeIdentifier(value) {
    const normalized = value === undefined || value === null ? "" : String(value).trim();
    return normalized && normalized !== "0" ? normalized : null;
}

function buildPrestashopImageUrl(source, productId, imageId, imageSize) {
    return apiUrl + `/prodotti/get/getImmagineProdottoPS/${source}/${productId}/${imageId}/${imageSize}`;
}

function ImgArt({
    prodIDps,
    imgIDps,
    size,
    fonte,
    fonteOrdine,
    allowBotImages = true,
    codVS,
    token,
    zoomable = true,
    zoomSize = "original",
    imageClassName,
    alt = "img",
}) {

    const [immagine, setImmagine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageSource, setImageSource] = useState("");
    const [prestashopImage, setPrestashopImage] = useState(null);
    const [zoomState, setZoomState] = useState({
        open: false,
        src: "",
        loading: false,
        error: "",
    });
    const closeZoom = useCallback(() => {
        setZoomState((current) => ({ ...current, open: false }));
    }, []);
    const { getAccessTokenSilently } = useAuth0();
    const fontePrestashop = String(fonte || fonteOrdine || "VIG").toUpperCase() === "BOT"
        ? "BOT"
        : "VIG";

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        const { signal } = controller;

        const setLoadedImage = (objectUrl, source, resolvedPrestashopImage = null) => {
            if (!isMounted) return;
            setImmagine(objectUrl);
            setImageSource(source);
            setPrestashopImage(resolvedPrestashopImage);
        };

        const loadImageBlob = async (url, source, resolvedPrestashopImage = null, headers) => {
            const cached = imageCache.get(url);
            if (cached) {
                setLoadedImage(cached, source, resolvedPrestashopImage);
                return true;
            }
            const response = await fetch(url, {
                ...(headers ? { headers } : {}),
                signal,
                priority: "low",
            });
            if (!response.ok) return false;
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            imageCache.set(url, objectUrl);
            setLoadedImage(objectUrl, source, resolvedPrestashopImage);
            return true;
        };

        const run = async () => {
            setImmagine(null);
            setLoading(true);
            setImageSource("");
            setPrestashopImage(null);

            const loadPrestashopImage = async (source, productId, imageId) => {
                if (!productId || !size || (source === "BOT" && !allowBotImages)) return false;

                let resolvedImageId = imageId;
                if (!resolvedImageId) {
                    try {
                        const defaultImageResponse = await fetch(
                            apiUrl + `/prodotti/ps/getIDImmagineProdottoPS/${source}/${productId}`,
                            { signal }
                        );
                        if (defaultImageResponse.ok) {
                            resolvedImageId = normalizeIdentifier(await defaultImageResponse.text());
                        }
                    } catch (error) {
                        resolvedImageId = null;
                    }
                }

                if (!resolvedImageId) return false;

                const resolvedPrestashopImage = {
                    source,
                    productId,
                    imageId: resolvedImageId,
                };
                const imageUrl = buildPrestashopImageUrl(
                    source,
                    productId,
                    resolvedImageId,
                    size
                );
                try {
                    return await loadImageBlob(imageUrl, "PS", resolvedPrestashopImage);
                } catch (error) {
                    return false;
                }
            };

            // In logistica questi ID fotografano l'articolo al momento dell'import
            // dell'ordine e hanno precedenza sulle associazioni prodotto correnti.
            // TODO: rivalutare questa priorita solo dopo avere verificato nel tempo
            // la copertura completa di R2 e delle associazioni MODA_Articoli.
            const storedProductId = normalizeIdentifier(prodIDps);
            const storedImageId = normalizeIdentifier(imgIDps);
            if (await loadPrestashopImage(fontePrestashop, storedProductId, storedImageId)) {
                return;
            }
            if (signal.aborted) return;

            let headers;
            if (codVS) {
                try {
                    const accessToken = token || await getAccessTokenSilently();
                    headers = { Authorization: `Bearer ${accessToken}` };
                    const libraryResponse = await fetch(
                        apiUrl + `/moda/articoli/${encodeURIComponent(codVS)}/immagini-library`,
                        { headers, signal }
                    );
                    if (libraryResponse.ok) {
                        const payload = await libraryResponse.json();
                        const firstImage = payload?.library?.images?.[0];
                        if (firstImage?.url) {
                            const assetUrl = apiUrl + firstImage.url;
                            if (await loadImageBlob(assetUrl, "R2", null, headers)) return;
                        }
                    }
                } catch (error) {
                    // Archivio o token non disponibili: prova il fallback MODA/Prestashop.
                }
            }
            if (signal.aborted) return;

            if (codVS) {
                try {
                    if (!headers) {
                        const accessToken = token || await getAccessTokenSilently();
                        headers = { Authorization: `Bearer ${accessToken}` };
                    }
                    const articleResponse = await fetch(
                        apiUrl + `/moda/articoli/${encodeURIComponent(codVS)}/immagini-fallback-vig`,
                        { headers, signal }
                    );
                    if (articleResponse.ok) {
                        const articlePayload = await articleResponse.json();
                        const modaVigProductId = normalizeIdentifier(articlePayload?.data?.idPs);
                        if (modaVigProductId) {
                            await loadPrestashopImage("VIG", modaVigProductId, null);
                            return;
                        }
                    }
                } catch (error) {
                    // Nessun'altra fonte disponibile.
                }
            }

        };
        run().finally(() => {
            if (isMounted) setLoading(false);
        });
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [
        allowBotImages,
        codVS,
        fontePrestashop,
        getAccessTokenSilently,
        imgIDps,
        prodIDps,
        size,
        token,
    ]);

    const openZoom = async () => {
        setZoomState({ open: true, src: "", loading: true, error: "" });
        if (imageSource === "R2") {
            setZoomState((current) => ({
                open: current.open,
                src: immagine,
                loading: false,
                error: "",
            }));
            return;
        }
        try {
            if (!prestashopImage) throw new Error("Riferimento Prestashop non disponibile");
            const zoomUrl = buildPrestashopImageUrl(
                prestashopImage.source,
                prestashopImage.productId,
                prestashopImage.imageId,
                zoomSize
            );
            const cached = imageCache.get(zoomUrl);
            if (cached) {
                setZoomState((current) => ({
                    open: current.open,
                    src: cached,
                    loading: false,
                    error: "",
                }));
                return;
            }
            const response = await fetch(zoomUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            imageCache.set(zoomUrl, objectUrl);
            setZoomState((current) => ({
                open: current.open,
                src: objectUrl,
                loading: false,
                error: "",
            }));
        } catch (error) {
            setZoomState((current) => ({
                open: current.open,
                src: "",
                loading: false,
                error: "Impossibile caricare l'immagine originale da Prestashop.",
            }));
        }
    };

    const image = (
        <img
            src={immagine}
            alt={alt}
            className={imageClassName}
            title={zoomable ? "Clicca per ingrandire" : undefined}
        />
    );

    if (!immagine) {
        return loading ? (
            <BlockSkeleton
                tone="soft"
                width="100%"
                height="auto"
                style={{
                    aspectRatio: "1 / 1",
                    maxWidth: size === "cart_default" ? 125 : 98,
                }}
            />
        ) : null;
    }

    return zoomable ? (
        <>
            <button
                type="button"
                className="modificaProdottiImmagineZoomTrigger"
                onClick={(event) => {
                    event.stopPropagation();
                    openZoom();
                }}
                aria-label={`${alt}: apri l'immagine originale`}
            >
                {image}
            </button>
            <ImageZoomModal
                open={zoomState.open}
                onClose={closeZoom}
                src={zoomState.src}
                alt={alt}
                loading={zoomState.loading}
                error={zoomState.error}
            />
        </>
    ) : image;
}

export default ImgArt;



